import { FC, memo } from 'react';

import { LocationStatePointsType, StreamGroupsCollectionPageParamsType } from 'application/pages/types';
import { RouterUtils, useLocation, useNavigate, useParams } from 'application/providers/RouterProvider';
import { SelectPlayerFlowType, useOnboardingActionContext, useOnboardingContext } from 'application/components/common';
import { TEXTS } from 'application/pages/texts';
import { STREAMS_CATEGORY_PATH, StreamsCategoryParamValues } from 'application/consts';
import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';

import { Country } from '@shared/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import { toast } from '@shared/ui';

import { useCurrentUserSberConnection } from '@entities/auth';

import useActionButtonsViewModel from './useActionButtonsViewModel';
import AcceptanceAdvancedPopper from './AcceptanceAdvancedPopper';
import { ActionButtonsProps } from './types';
import { showCopyStreamError } from './utils';

const ActionButtons: FC<ActionButtonsProps> = ({
  selectedPoints,
  isAcceptanceAdvancedPending,
  isLoading,
  isStreamBindButtonLoading,
  isStreamBindButtonDisabled,
  streamSetToPointsLoading,
  replaceModal,
  setIsStreamBindButtonLoading,
  handleChangeStream,
  copyTemplateStream,
  getSavedStreams,
  getStream,
  onboardingPassedRequest,
  refetchFeaturesOnboarding,
  readStreamWithThematic,
}) => {
  const hasSberConnection = useCurrentUserSberConnection();

  const { isTemplateMode, currentPoint, flowType } = useOnboardingContext();
  const { setInFlow, setFlowType, setCurrentPointStream, setCurrentPoint } = useOnboardingActionContext();
  const navigate = useNavigate();
  const location = useLocation<LocationStatePointsType>();
  const pathParams = useParams<StreamGroupsCollectionPageParamsType>();
  const { openModal } = useModalContext();

  const [pointFromLocation] = location.state?.pointsFromLocation || [];
  const targetStreamId = pathParams.id;
  const collectionId = pathParams.collectionId;

  const { handleBindStreamClick, handleAddPointClick, openAcceptanceAdvancedSigning } = useActionButtonsViewModel({
    targetStreamId,
    collectionId,
    isTemplateMode,
    selectedPoints,
    currentPoint,
    isFullOnboarding: flowType === SelectPlayerFlowType.FULL_ONBOARDING,
    setCurrentPoint,
    setCurrentPointStream,
    setInFlow,
    setFlowType,
    openModal,
    replaceModal,
    redirectToStreamCategoryPage: (streamId: string) => {
      navigate(
        RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: StreamsCategoryParamValues.SAVED,
          collectionId: 0,
          id: streamId,
        })
      );
    },
    showCopyStreamWithThematicError: () => {
      toast.error(TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_NOTIFICATION_ERROR);
    },
    showCopyStreamWithThematicSuccess: () => {
      toast.info(TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_NOTIFICATION_SUCCESS);
    },
    showCopyStreamError: (country: Country) => {
      showCopyStreamError(country);
    },
    setIsStreamBindButtonLoading,
    handleChangeStream,
    copyTemplateStream,
    getSavedStreams,
    getStream,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    readStreamWithThematic,
  });

  const handleAcceptanceAdvancedSigningClick = () => {
    openModal(ModalTypes.SBER_REDIRECT_WARNING, {
      onConfirm: () => {
        openAcceptanceAdvancedSigning();
      },
    });
  };

  return (
    <>
      <Button
        key="button_bind_stream_modal"
        elementId="button_bind_stream_modal"
        type={ButtonType.PRIMARY}
        isLoading={isLoading || isStreamBindButtonLoading || streamSetToPointsLoading}
        onClick={handleBindStreamClick}
        isDisabled={isStreamBindButtonDisabled}>
        {TEXTS.MODAL_BIND_BUTTON_SUBMIT_TEXT}
      </Button>
      {!pointFromLocation && (
        <AcceptanceAdvancedPopper
          isAcceptanceAdvancedPending={isAcceptanceAdvancedPending}
          hasSberConnection={hasSberConnection}
          handleAddPointClick={handleAddPointClick}
          handleAcceptanceAdvancedSigningClick={handleAcceptanceAdvancedSigningClick}
        />
      )}
    </>
  );
};

export default memo(ActionButtons);
